import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellDoubleClickedEvent, type RowClassParams, type IRowNode } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, FormControl, MenuItem, Select, Tooltip } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import SkuInfoPopup from "@user/views/popup/popup_sku_info";

interface State {
  gridApi: GridApi | null,
  isContinueSearch: boolean,
  skuNo: string,
  isShowSkuInfo: boolean
}

const defState: State = {
  gridApi: null,
  isContinueSearch: true,
  skuNo: "",
  isShowSkuInfo: false
};

interface SearchParams {
  workType: string,
  workStationId: string,
  workNoMfcLike: string,
  skuNoExLike: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  workType: "",
  workStationId: "",
  workNoMfcLike: "",
  skuNoExLike: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

const validationMsg = [
  {id: "reqInfos", type: "NotBlank", msg: "작업을 선택해 주세요."},
  {id: "equipWorkNo", type: "InvalidStatus", msg: "작업대기 상태만 작업취소할 수 있습니다."}
];

function WorkStatus(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Track No", field: "workNoMfc", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업대", field: "workStationId", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "작업구분", field: "workType", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workType") },
    { headerName: "작업지시일시", field: "workReqDate", width: 200, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.date("yyyy-MM-dd HH:mm:ss"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "SKU No", field: "skuNoEx", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "출발위치", field: "startPos", width: 150, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        return (p.data.workTypeEquip == "I" ? commonCode.getCodeValue("workStationId", p.data.startPos) : valueFormatter.locationId(p.data.startPos));
      }
    },
    { headerName: "도착위치", field: "endPos", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        return (p.data.workTypeEquip == "I" ? valueFormatter.locationId(p.data.endPos) : commonCode.getCodeValue("workStationId", p.data.endPos));
      }
    },
    { headerName: "설비", field: "workEquip", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("workEquip"), filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "설비상태", field: "workStatusEquip", width: 120, cellStyle: {textAlign: "center", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        return (p.data.workStatusEquip == "START" || p.data.workStatusEquip == "ACTIVE" ? p.data.currPos : commonCode.getCodeValue("workStatusEquip", p.data.workStatusEquip));
      }
    },
    { headerName: "오류내용", headerClass: "hd-ta-left", field: "", width: 350, cellStyle: {textAlign: "left", cursor: "pointer"}, filter: true, filterParams: {buttons: ["reset"]},
      valueGetter: (p: any) => {
        return p.data.errorNo || p.data.delayCause;
      }
    },
    { headerName: "작업완료", colId: "completeWork", field: "", width: 100, cellStyle: kutil.aggridButtonCellStyle,
      cellRenderer: (p: any) => {
        if (p.data.workStatusEquip != "ERROR") {
          if (!auth.write) return null;
          return (<button className="icon-button" onClick={() => completeWork(p.data)}>[완료]</button>);
        }
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    getRowId: (p: any) => String(p.data.equipWorkNo),
    getRowStyle: (p: RowClassParams) => {
      if (p.data.workStatusEquip == "ERROR") {
        return {"backgroundColor": "#FFCBCB"};
      }
    },
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        const startPos = n.data.startPos?.length > 3 ? n.data.startPos.substring(1, 2) : n.data.startPos;
        const currPos = n.data.currPos?.length > 3 ? n.data.currPos.substring(1, 2) : n.data.currPos;
        const isFirstStep = startPos == currPos;
        // 출고작업 & 첫단계 & (IDLE 또는 DELAY)
        return n.data.workTypeEquip == "O" && isFirstStep && (n.data.workStatusEquip == "IDLE" || n.data.workStatusEquip == "DELAY");
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellDoubleClicked: (e: CellDoubleClickedEvent) => {
      const colId = e.column.getColId();
      if (colId === "ag-Grid-SelectionColumn" || colId === "completeWork") return;
      openPopupSkuInfo(e.data.skuNo);
    },
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  useEffect(() => {
    if (!isActiveTab) {
      return;
    }

    if (state.isContinueSearch) {
      const intervalId = setInterval(() => {
        searchInfos();
      }, 5000);

      return () => clearInterval(intervalId);
    }
  }, [state.isContinueSearch, isActiveTab]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/work/work_status.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }

  async function doSearch(_e: any, reqParams: any|null = null) {
    setState(prev => ({ ...prev, isContinueSearch: false }));

    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/work/work_status", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/work/work_status", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/work/work_status", {});
  }

  async function doSearchContinue(search: boolean) {
    setState(prev => ({ ...prev, isContinueSearch: search }));
    if (search) {
      await searchInfos();
    }
  }

  async function cancelWork() {
    const prevContinue = state.isContinueSearch;
    setState(prev => ({ ...prev, isContinueSearch: false }));

    try {
      const selectedInfos = state.gridApi!.getSelectedRows();
      if (selectedInfos.length == 0) {
        kutil.alert("작업을 선택해 주세요.");
        return;
      }

      if (await kutil.confirm("작업취소하시겠습니까?") == false) {
        return false;
      }

      const data = await kutil.sendPost("/api/work/work_status.proc?mode=cancelWork", selectedInfos, {
        validationMsg: validationMsg
      });
      await kutil.alert("작업취소되었습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    } finally {
      setState(prev => ({ ...prev, isContinueSearch: prevContinue }));
    }
  }

  async function completeWork(info: any) {
    const prevContinue = state.isContinueSearch;
    setState(prev => ({ ...prev, isContinueSearch: false }));

    try {
      if (await kutil.confirm("작업완료하시겠습니까?") == false) {
        return false;
      }

      info.workStatusEquip = "DONE";
      info.workStatusEquipStep = "FORCE_DONE";
      const data = await kutil.sendPost("/api/test/mfc_test.proc?mode=updateEquipWork", info, {
      });
      await kutil.alert("작업완료하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    } finally {
      setState(prev => ({ ...prev, isContinueSearch: prevContinue }));
    }
  }

  async function openPopupSkuInfo(skuNo: string) {
    setState(prev => ({ ...prev, isContinueSearch: false, isShowSkuInfo: true, skuNo: skuNo }));
  }

  async function closePopupSkuInfo(_data: any) {
    setState(prev => ({ ...prev, isShowSkuInfo: false }));
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>작업상태</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            {state.isContinueSearch == true && <Button variant="outlined" onClick={() => doSearchContinue(false)}>연속조회중지</Button>}
            {state.isContinueSearch == false && <Button variant="outlined" onClick={() => doSearchContinue(true)}>연속조회시작</Button>}
            { auth.write &&
            <Button variant="contained" onClick={cancelWork}>작업취소</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>작업구분</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.workType}
                onChange={(e) => {doSearch(e, {workType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.workType as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>작업대</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.workStationId}
                onChange={(e) => {doSearch(e, {workStationId: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.workStationId as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>Track No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.workNoMfcLike}
              onChange={(e) => {updateSearchParams({ workNoMfcLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="Track No"
            />
          </div>
          <div>
            <p>SKU No</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.skuNoExLike}
              onChange={(e) => {updateSearchParams({ skuNoExLike: e.target.value })}}
              onKeyDown={(e) => { kutil.onKeyDownForInput(e, doSearch) }}
              placeholder="SKU No"
            />
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 그리드 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              getRowId={gridOptions.getRowId}
              getRowStyle={gridOptions.getRowStyle}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellDoubleClicked={gridCallbacks.onCellDoubleClicked}
            />
          </div>
        </AgGridProvider>
        {/*// 그리드 */}
        {state.isShowSkuInfo && <SkuInfoPopup isShow={state.isShowSkuInfo} onClose={closePopupSkuInfo} data={{skuNo: state.skuNo}} />}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(WorkStatus);
